import React, { useCallback, useMemo, useState } from "react";

import ShellCard from "../../../../../components/ShellCard";
import { useTheme } from "../../../../../hooks/theme";
import { formatCep } from "../../../../../utils/formatDoc";
import { ListaOcorrencias, useOcorrencias } from "../Ocorrencias";
import {
  buildSteps,
  getDeliveryStatus,
  getTimelineProgress,
  hasDate,
  rotuloCidade,
} from "../../status";
// Mesmo componente do tracking público. As cores saem de tokens; aqui ele usa
// a variante compacta e, no tema escuro, o fundo escuro.
import MapaRotaEstimada from "../../../../Public/Tracking/components/MapaRotaEstimada";

const COPY_TIMEOUT = 1800;

const getDisplayValue = (value) => value || "-";

const getTransportadoraName = (item) =>
  item.transportadora ||
  item.nome_transportadora ||
  item.transportadora_nome ||
  item.nomeTransportadora ||
  item.transportadoraFantasia ||
  item.fantasia_transportadora ||
  null;

const normalizeAddressPart = (value) => {
  if (!value) return null;
  const normalized = String(value).trim();
  if (!normalized || ["NA", "N/A", "NULL", "-"].includes(normalized.toUpperCase())) {
    return null;
  }
  return normalized;
};

const getDeliveryAddress = (item) => {
  if (item.endereco_entrega) return item.endereco_entrega;

  const parts = [
    item.logradouro_entrega,
    item.numero_entrega,
    item.complemento_entrega,
    item.bairro_entrega,
  ].map(normalizeAddressPart).filter(Boolean);

  return parts.length > 0 ? parts.join(", ") : null;
};

const ChaveAcesso = ({ chave }) => {
  const [copiado, setCopiado] = useState(false);

  const copiar = useCallback(async () => {
    if (!chave || !navigator.clipboard) return;

    try {
      await navigator.clipboard.writeText(chave);
      setCopiado(true);
      setTimeout(() => setCopiado(false), COPY_TIMEOUT);
    } catch (_) {
      setCopiado(false);
    }
  }, [chave]);

  if (!chave) return <dd>-</dd>;

  return (
    <dd>
      <span className="tracking-key">
        <code>{chave}</code>
        <button
          type="button"
          className={`tracking-mini-button${copiado ? " is-copied" : ""}`}
          onClick={copiar}
          aria-label="Copiar chave de acesso"
        >
          <i className={copiado ? "tim-icons icon-check-2" : "tim-icons icon-single-copy-04"} />
          {copiado ? "Copiada" : "Copiar"}
        </button>
      </span>
    </dd>
  );
};

// Link da página pública de rastreio (views/Public/Tracking), no mesmo host do
// Hub. Ela só aceita a chave da NF-e (44 dígitos), então sem chave não há link.
const buildPublicLink = (chave) =>
  `${window.location.origin}/public/tracking?chaveNf=${encodeURIComponent(chave)}`;

const CopiarLinkPublico = ({ chave }) => {
  const [copiado, setCopiado] = useState(false);

  const copiar = useCallback(async () => {
    if (!chave || !navigator.clipboard) return;

    try {
      await navigator.clipboard.writeText(buildPublicLink(chave));
      setCopiado(true);
      setTimeout(() => setCopiado(false), COPY_TIMEOUT);
    } catch (_) {
      setCopiado(false);
    }
  }, [chave]);

  if (!chave || String(chave).length !== 44) return null;

  return (
    <button
      type="button"
      className={`tracking-mini-button tracking-mini-button--lg${copiado ? " is-copied" : ""}`}
      onClick={copiar}
      title="Página de rastreio que o destinatário pode abrir sem login"
    >
      <i className={copiado ? "tim-icons icon-check-2" : "tim-icons icon-link-72"} />
      {copiado ? "Link copiado" : "Copiar link público"}
    </button>
  );
};

const STEP_EMPTY = {
  previsao: "Não informada",
  entrega: "Pendente",
};

const Etapas = ({ item, tone }) => {
  const steps = buildSteps(item);
  const progress = getTimelineProgress(steps);
  const semPrevisao = !hasDate(item.dt_previsao_entrega) && !hasDate(item.dt_entrega);

  return (
    <ol
      className={`tracking-steps tracking-steps--${tone}`}
      style={{ "--tracking-progress": `${progress}%` }}
      aria-label="Etapas da entrega"
    >
      <li className="tracking-steps__track" aria-hidden="true">
        <span />
      </li>
      {steps.map((step, index) => {
        const done = hasDate(step.date);
        const waiting = step.key === "previsao" && semPrevisao;
        const className = [
          "tracking-steps__step",
          done ? "is-done" : null,
          waiting ? "is-waiting" : null,
        ].filter(Boolean).join(" ");

        return (
          <li key={step.key} className={className}>
            <span className="tracking-steps__dot">
              {done ? <i className="tim-icons icon-check-2" /> : index + 1}
            </span>
            <strong>{step.label}</strong>
            <small>{done ? step.date : STEP_EMPTY[step.key] || "-"}</small>
          </li>
        );
      })}
    </ol>
  );
};

const Historico = ({ idNf, transportadora }) => {
  const { ocorrencias, error } = useOcorrencias(idNf);
  const total = ocorrencias ? ocorrencias.length : 0;

  return (
    <div className="tracking-history">
      <div className="tracking-history__head">
        <h4>Histórico de ocorrências</h4>
        {total > 0 ? (
          <span>
            {total} {total === 1 ? "ocorrência" : "ocorrências"}
          </span>
        ) : null}
      </div>
      <ListaOcorrencias
        ocorrencias={ocorrencias}
        error={error}
        compact
        emptyMessage={
          transportadora
            ? `Nenhuma ocorrência ainda. Quando a ${transportadora} enviar a primeira, ela aparece aqui.`
            : "Nenhuma ocorrência ainda. Quando a transportadora enviar a primeira, ela aparece aqui."
        }
      />
    </div>
  );
};

const ResultadoEntrega = ({ item, darkMode }) => {
  const status = getDeliveryStatus(item);
  const transportadora = getTransportadoraName(item);
  const enderecoEntrega = getDeliveryAddress(item);
  const origemLabel = rotuloCidade(item.cidade_origem, item.uf_origem);
  const destinoLabel = rotuloCidade(item.cidade_entrega, item.uf_entrega);

  // Objetos estáveis: o mapa remonta quando origem/destino mudam de referência.
  const origem = useMemo(
    () => ({ ibge: item.ibge_origem, cidade: item.cidade_origem, uf: item.uf_origem }),
    [item.ibge_origem, item.cidade_origem, item.uf_origem]
  );
  const destino = useMemo(
    () => ({ ibge: item.ibge_entrega, cidade: item.cidade_entrega, uf: item.uf_entrega }),
    [item.ibge_entrega, item.cidade_entrega, item.uf_entrega]
  );

  const cidadeEntrega = [formatCep(item.cep_entrega), destinoLabel]
    .filter(Boolean)
    .join(" · ");

  return (
    <article className="tracking-result">
      <header className="tracking-result__head">
        <div className="tracking-result__title">
          <span className="tracking-eyebrow">Rastreio Everlog #{getDisplayValue(item.id)}</span>
          <h3>
            NF {getDisplayValue(item.numero)}
            {item.numero_pedido ? <span> · Pedido {item.numero_pedido}</span> : null}
          </h3>
          <p>{status.description}</p>
        </div>
        <div className="tracking-result__side">
          <span className={`tracking-status-badge tracking-status-badge--${status.tone}`}>
            {status.label}
          </span>
          <CopiarLinkPublico chave={item.chave_acesso} />
        </div>
      </header>

      {origemLabel || destinoLabel || transportadora ? (
        <div className="tracking-route-strip">
          {origemLabel || destinoLabel ? (
            <span>
              <b>{origemLabel || "Origem não informada"}</b>
              <span className="tracking-route-strip__arrow" aria-hidden="true"> → </span>
              <b>{destinoLabel || "Destino não informado"}</b>
            </span>
          ) : null}
          {transportadora ? (
            <span>
              Transportadora <b>{transportadora}</b>
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="tracking-result__info">
        <Etapas item={item} tone={status.tone} />
        <dl className="tracking-details">
          <dt>Destinatário</dt>
          <dd>{getDisplayValue(item.destinatario)}</dd>
          <dt>Entrega</dt>
          <dd>
            {getDisplayValue(enderecoEntrega)}
            {cidadeEntrega ? <small>{cidadeEntrega}</small> : null}
          </dd>
          <dt>Remetente</dt>
          <dd>{getDisplayValue(item.remetente)}</dd>
          <dt>Chave NF-e</dt>
          <ChaveAcesso chave={item.chave_acesso} />
        </dl>
      </div>

      <Historico idNf={item.id} transportadora={transportadora} />

      {/* Mapa por último e em largura total: é ilustrativo (linha reta entre
          municípios), então não disputa espaço com os dados e o histórico. */}
      <div className="tracking-result__map">
        <MapaRotaEstimada
          origem={origem}
          destino={destino}
          tema={darkMode ? "escuro" : undefined}
          compacto
        />
      </div>
    </article>
  );
};

const CardResult = ({ data }) => {
  const { darkMode } = useTheme();

  return (
    <ShellCard
      className="tracking-results"
      eyebrow="Resultado"
      title={data.length === 1 ? "Entrega encontrada" : "Entregas encontradas"}
      action={
        <span className="tracking-results-count">
          {data.length} {data.length === 1 ? "entrega" : "entregas"}
        </span>
      }
    >
      <div className="tracking-results__list">
        {data.map((item, index) => (
          <ResultadoEntrega key={item.id || index} item={item} darkMode={darkMode} />
        ))}
      </div>
    </ShellCard>
  );
};

export default CardResult;
